/**
 * Marriage bot: solver-driven show/finish detection, meld-protecting discards and selective discard pickup.
 */
import type { CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { jokerContext, type MarriageAction, type MarriageLegal, type MarriageView } from './engine.js';
import { candidateMelds, cardUtility, findDisjointMelds, isMeld, partitionIntoMelds, type JokerContext } from './melds.js';

function ctxOf(view: MarriageView): JokerContext | null {
  return view.tiplu ? jokerContext({ tiplu: view.tiplu, config: view.config }) : null;
}

function protectedCards(hand: CardId[], ctx: JokerContext | null, hasShown: boolean): Set<number> {
  const keep = new Set<number>();
  const want = hasShown ? 4 : 3;
  for (let n = want; n >= 1; n--) {
    const melds = findDisjointMelds(hand, n, hasShown ? ctx : null, !hasShown);
    if (melds) {
      melds.flat().forEach((i) => keep.add(i));
      break;
    }
  }
  return keep;
}

function chooseDiscard(view: MarriageView, legal: MarriageLegal, ctx: BotContext): CardId {
  const me = view.players[view.seat!]!;
  const jctx = me.hasShown ? ctxOf(view) : null;
  const hand = view.hand;
  const keep = protectedCards(hand, jctx, me.hasShown);
  const util = cardUtility(hand, jctx);
  const candidates = hand.map((c, i) => ({ c, i })).filter(({ c, i }) => c !== legal.lockedCard && !keep.has(i));
  const pool = candidates.length ? candidates : hand.map((c, i) => ({ c, i })).filter(({ c }) => c !== legal.lockedCard);
  if (chance(ctx, ctx.profile.mistakeRate)) return pool[ctx.rng.int(pool.length)]!.c;
  pool.sort((a, b) => util[a.i]! - util[b.i]!);
  return pool[0]!.c;
}

function tryFinish(view: MarriageView, legal: MarriageLegal): MarriageAction | null {
  const jctx = ctxOf(view);
  const hand = view.hand;
  for (let i = 0; i < hand.length; i++) {
    if (hand[i] === legal.lockedCard) continue;
    const rest = hand.filter((_, k) => k !== i);
    const groups = partitionIntoMelds(rest, jctx);
    if (groups) return { type: 'finish', discard: hand[i]!, groups };
  }
  return null;
}

export const marriageBot: BotEngine<MarriageView, MarriageLegal, MarriageAction> = {
  decideAction(view, legal, ctx) {
    const me = view.players[view.seat!]!;
    if (legal.canDrawStock || legal.canDrawDiscard) {
      if (legal.canDrawDiscard && view.discardTop && ctx.difficulty !== 'BEGINNER') {
        const hand = [...view.hand, view.discardTop];
        const idx = hand.length - 1;
        const jctx = me.hasShown ? ctxOf(view) : null;
        const useful = candidateMelds(hand, jctx, !me.hasShown).some((m) => m.includes(idx));
        if (useful || !legal.canDrawStock) return { type: 'draw', from: 'discard' };
      }
      return { type: 'draw', from: legal.canDrawStock ? 'stock' : 'discard' };
    }
    if (legal.canShow) {
      const melds = findDisjointMelds(view.hand, 3, null, true);
      if (melds) return { type: 'show', groups: melds.map((m) => m.map((i) => view.hand[i]!)) };
    }
    if (legal.canFinish) {
      const fin = tryFinish(view, legal);
      if (fin) return fin;
    }
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    return { type: 'discard', card: chooseDiscard(view, legal, ctx) };
  },

  evaluateState(view) {
    const me = view.players[view.seat!];
    if (!me) return 0;
    const jctx = me.hasShown ? ctxOf(view) : null;
    let melds = 0;
    for (let n = 4; n >= 1; n--) {
      if (findDisjointMelds(view.hand, n, jctx, !me.hasShown)) {
        melds = n;
        break;
      }
    }
    const progress = (me.shown.length + melds) / 7;
    return Math.min(1, progress);
  },

  calculateRisk(view) {
    // Risk of losing big: not having shown while others have.
    const me = view.players[view.seat!];
    const othersShown = view.players.filter((p) => p && p.seat !== view.seat && p.hasShown).length;
    return me && !me.hasShown ? Math.min(1, 0.3 + othersShown * 0.2) : 0.2;
  },

  handleTimeout(view, legal) {
    if (legal.canDrawStock) return { type: 'draw', from: 'stock' };
    if (legal.canDrawDiscard) return { type: 'draw', from: 'discard' };
    const card = view.hand.find((c) => c !== legal.lockedCard) ?? view.hand[0]!;
    return { type: 'discard', card };
  },
};

export { isMeld };
